/* oxlint-disable lingui/no-unlocalized-strings -- TODO(i18n): translate (debt) */
/* oxlint-disable no-restricted-syntax */
import { createRoute } from "@tanstack/solid-router";
import { createMemo, For, Show } from "solid-js";
import { chronosRoute } from "../chronos";
import { PageContainer } from "@fleet/ui";
import { PulseTimer } from "@/features/chronos-sync/components/pulse-timer";
import { ActivityTimelineView } from "@/features/chronos-sync/components/activity-timeline-view";
import { ClockEmptyIllustration } from "@/components/ui/illustrations";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useWorkspace } from "@/features/chronos-sync/workspace-context";
import { WorkspaceSwitcher } from "@/features/chronos-sync/components/workspace-switcher";
import { createPowerSyncQuery } from "@time-tracker/shared/solid";
import {
  and,
  eq,
  project,
  type Project,
  sql,
  time_entry,
  type TimeEntry,
} from "@time-tracker/powersync";
import {
  computeWeeklyGoalProgress,
  liveProjectFilters,
  liveTimeEntryFilters,
  queryKeys,
} from "@time-tracker/shared";
import { getWeeklyGoalHours } from "@/features/chronos-sync/weekly-goal";
import { WeeklyGoalCard } from "@/features/chronos-sync/components/weekly-goal-card";

// Named constants to avoid magic numbers
const DAYS_IN_WEEK = 7;
const HOURS_IN_DAY = 24;
const MINS_IN_HOUR = 60;
const SECS_IN_MIN = 60;
const MS_IN_SEC = 1000;
const TOP_PROJECTS_LIMIT = 3;

function ChronosDashboardPage() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;
  const { activeWorkspaceId } = useWorkspace();
  const { t } = useI18n();
  // Safe first-name accessor — session.data is typed Session via the machine getter
  const firstName = () => {
    const name: string | null | undefined = session.data?.user.name;
    if (!name) {
      return undefined;
    }
    return name.split(" ")[0];
  };

  // 1. Query projects of the active workspace
  const projectsQuery = createPowerSyncQuery<Project>({
    queryKey: () => queryKeys.chronos.dashboardProjects(userId(), activeWorkspaceId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(project)
        .where(and(eq(project.workspace_id, activeWorkspaceId()), ...liveProjectFilters()));
    },
    enabled: () => Boolean(drizzle()) && Boolean(activeWorkspaceId()),
    suspense: false,
  });

  // 2. Query weekly time entries (last 7 days)
  const weeklyEntriesQuery = createPowerSyncQuery<TimeEntry>({
    queryKey: () => queryKeys.chronos.dashboardWeeklyEntries(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      const sevenDaysAgo = new Date(
        Date.now() - DAYS_IN_WEEK * HOURS_IN_DAY * MINS_IN_HOUR * SECS_IN_MIN * MS_IN_SEC,
      );
      return dbInstance
        .select()
        .from(time_entry)
        .where(
          and(
            eq(time_entry.user_id, userId()),
            sql`time_entry.start_time >= ${sevenDaysAgo.toISOString()}`,
            ...liveTimeEntryFilters(),
          ),
        );
    },
    enabled: () => Boolean(userId()) && Boolean(drizzle()),
    suspense: false,
  });

  // 3. Compute Top 3 projects
  const topProjects = createMemo(() => {
    const list = projectsQuery.data ?? [];
    const entries = weeklyEntriesQuery.data ?? [];

    const weeklyMsMap = new Map<string, number>();
    for (const entry of entries) {
      if (!entry.project_id || !entry.start_time) {
        continue;
      }
      const start = new Date(entry.start_time).getTime();
      const end = entry.end_time ? new Date(entry.end_time).getTime() : Date.now();
      if (end > start) {
        weeklyMsMap.set(entry.project_id, (weeklyMsMap.get(entry.project_id) ?? 0) + (end - start));
      }
    }

    const projectsWithTime = [];
    for (const p of list) {
      const ms = weeklyMsMap.get(p.id) ?? 0;
      const totalHoursDecimal = ms / (MS_IN_SEC * SECS_IN_MIN * MINS_IN_HOUR);
      const hours = Math.floor(totalHoursDecimal);
      const mins = Math.floor((totalHoursDecimal - hours) * MINS_IN_HOUR);

      projectsWithTime.push({
        ...p,
        ms,
        durationStr: `${hours}h ${mins}m`,
      });
    }

    // Use toSorted to avoid mutating the array, then slice top projects
    return projectsWithTime.toSorted((a, b) => b.ms - a.ms).slice(0, TOP_PROJECTS_LIMIT);
  });

  // 4. Weekly goal progress (device-local target; pure math from the shared kit).
  // Read once per mount — navigation to/from settings remounts the dashboard.
  const weeklyGoalHours = getWeeklyGoalHours();
  const weeklyGoal = createMemo(() =>
    computeWeeklyGoalProgress({
      blocks: weeklyEntriesQuery.data ?? [],
      goalHours: weeklyGoalHours,
    }),
  );
  return (
    <PageContainer
      maxWidth="7xl"
      class="space-y-8"
      containerClass="bg-[#0b0b10] text-white pb-[calc(5.5rem+var(--safe-area-inset-bottom))]"
    >
      {/* Greeting heading — spec: dashboard_principal/code.html */}
      <section class="mb-2 flex items-start justify-between gap-4">
        <div>
          <h1 class="text-[28px] font-semibold leading-[34px] text-white tracking-tight">
            {t(msg`Hi, {name}`, { name: firstName() ?? t(msg`team`) })}
          </h1>
          <p class="text-sm text-white/50 mt-1">{t(msg`Ready to focus today.`)}</p>
        </div>
        <WorkspaceSwitcher />
      </section>
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Side: Pulse Timer & Widgets */}
        <div class="lg:col-span-4 lg:sticky lg:top-6 space-y-6">
          <PulseTimer />

          <Show when={weeklyGoal().goalHours > 0}>
            <WeeklyGoalCard progress={weeklyGoal()} />
          </Show>

          {/* Proyectos Recientes — horizontal snap scroll, spec: dashboard_principal/code.html */}
          <div class="bg-[#121212] border border-white/10 rounded-[24px] p-6 shadow-none">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-sm font-semibold text-white">{t(msg`Recent Projects`)}</h3>
              <a
                href="/chronos/projects"
                class="font-mono text-[11px] text-primary flex items-center gap-1 hover:underline"
              >
                {t(msg`View all`)} →
              </a>
            </div>
            <Show
              when={topProjects().length > 0}
              fallback={
                <div class="text-center py-6 motion-safe:animate-fade-in">
                  <ClockEmptyIllustration class="h-24 w-24 mx-auto mb-1" />
                  <p class="text-xs text-muted-foreground font-mono">
                    {t(msg`No activity this week`)}
                  </p>
                </div>
              }
            >
              {/* Snap scroll strip — -mx-6 px-6 to bleed to card edges */}
              <div class="flex overflow-x-auto gap-4 -mx-6 px-6 pb-2 snap-x snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                <For each={topProjects()}>
                  {(proj) => {
                    const defaultAccent = {
                      dot: "#f1b12b",
                      dotGlow: "rgba(241,177,43,0.55)",
                      text: "#f1b12b",
                    };
                    const accentMap: Record<
                      string,
                      { dot: string; dotGlow: string; text: string }
                    > = {
                      "bg-blue-500": defaultAccent,
                      "bg-purple-500": {
                        dot: "#cd5dff",
                        dotGlow: "rgba(205,93,255,0.6)",
                        text: "#ebb2ff",
                      },
                      "bg-emerald-500": {
                        dot: "#01f5a0",
                        dotGlow: "rgba(1,245,160,0.6)",
                        text: "#ceffdf",
                      },
                      "bg-amber-500": {
                        dot: "#f5a623",
                        dotGlow: "rgba(245,166,35,0.5)",
                        text: "#fdd9a0",
                      },
                    };
                    const accent = accentMap[proj.color ?? ""] ?? defaultAccent;
                    return (
                      <div class="snap-start shrink-0 w-[200px] bg-[#201f1f] border border-white/[0.08] rounded-[16px] p-4 flex flex-col justify-between min-h-[110px] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] hover:bg-white/[0.04] transition-colors">
                        <div class="flex items-center gap-2">
                          <div
                            class="w-2.5 h-2.5 rounded-full shrink-0"
                            style={{
                              "background-color": accent.dot,
                              "box-shadow": `0 0 8px ${accent.dotGlow}`,
                            }}
                          />
                          <span class="font-mono text-[10px] text-white/50 uppercase tracking-widest truncate">
                            {proj.code ?? proj.name}
                          </span>
                        </div>
                        <div class="mt-3">
                          <h4 class="text-sm font-semibold text-white truncate">{proj.name}</h4>
                          <p class="font-mono text-[13px] mt-1" style={{ color: accent.text }}>
                            {proj.durationStr}
                            <span class="text-white/30 text-[10px] ml-1">{t(msg`this wk`)}</span>
                          </p>
                        </div>
                      </div>
                    );
                  }}
                </For>
              </div>
            </Show>
          </div>
        </div>

        {/* Right Side: Activity Timeline View */}
        <div class="lg:col-span-8">
          <ActivityTimelineView />
        </div>
      </div>
    </PageContainer>
  );
}

// The dashboard hosts the PulseTimer (preselection `?project=&task=&desc=`) and the
// ActivityTimelineView (time-entry dialogs, same params as the timeline route).
// We validate both sets here so their URLs reopen the state.
interface ChronosIndexSearch {
  project?: string;
  task?: string;
  desc?: string;
  dialog?: "entry-new" | "entry-edit";
  confirm?: "delete-entry";
  id?: string;
  wsdialog?: "new";
}

const isNonEmpty = (v: unknown): v is string => typeof v === "string" && v.trim() !== "";

export const chronosIndexRoute = createRoute({
  getParentRoute: () => chronosRoute,
  path: "/",
  validateSearch: (raw: Record<string, unknown>): ChronosIndexSearch => {
    const dialog =
      raw.dialog === "entry-new" || raw.dialog === "entry-edit" ? raw.dialog : undefined;
    const confirm = raw.confirm === "delete-entry" ? raw.confirm : undefined;
    return {
      project: isNonEmpty(raw.project) ? raw.project : undefined,
      task: isNonEmpty(raw.task) ? raw.task : undefined,
      desc: typeof raw.desc === "string" && raw.desc !== "" ? raw.desc : undefined,
      dialog,
      confirm,
      id: (dialog === "entry-edit" || confirm) && isNonEmpty(raw.id) ? raw.id : undefined,
      wsdialog: raw.wsdialog === "new" ? "new" : undefined,
    };
  },
  component: ChronosDashboardPage,
});
